import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { OpenSheetMusicDisplay } from 'opensheetmusicdisplay';
import CollapsibleSection from './CollapsibleSection';
import { loadSoundfontInstrument } from '../utils/soundfontInstruments';
import { logEarTrainingStart, logEarTrainingComplete } from '../utils/activityLogger';
import * as Tone from 'tone';

// Diatonic letter names and their semitone offset from C
const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const LETTER_SEMITONES = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Root pitches the drill can start from
const NATURAL_ROOTS = [
  { letter: 'C', alter: 0 },
  { letter: 'D', alter: 0 },
  { letter: 'E', alter: 0 },
  { letter: 'F', alter: 0 },
  { letter: 'G', alter: 0 },
  { letter: 'A', alter: 0 },
  { letter: 'B', alter: 0 }
];

const CHROMATIC_ROOTS = [
  { letter: 'C', alter: 0 },
  { letter: 'D', alter: -1 },
  { letter: 'D', alter: 0 },
  { letter: 'E', alter: -1 },
  { letter: 'E', alter: 0 },
  { letter: 'F', alter: 0 },
  { letter: 'F', alter: 1 },
  { letter: 'G', alter: 0 },
  { letter: 'A', alter: -1 },
  { letter: 'A', alter: 0 },
  { letter: 'B', alter: -1 },
  { letter: 'B', alter: 0 }
];

const midiFromSpelling = (letter, alter, octave) =>
  (octave + 1) * 12 + LETTER_SEMITONES[letter] + alter;

const accidentalSymbol = (alter) => {
  if (alter === 0) return '';
  if (alter > 0) return '♯'.repeat(alter);
  return '♭'.repeat(-alter);
};

const spellingToDisplay = ({ letter, alter, octave }) =>
  `${letter}${accidentalSymbol(alter)}${octave}`;

// Playback name (soundfont + Tone both understand sharp spellings)
const midiToSharpName = (midi) =>
  `${SHARP_NAMES[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;

// Fall back to a plain sharp/flat name when strict interval spelling would
// need a triple accidental (only reachable from odd root/interval combos)
const simpleSpelling = (midi, preferFlat) => {
  const pc = ((midi % 12) + 12) % 12;
  const octave = Math.floor(midi / 12) - 1;
  const sharpName = SHARP_NAMES[pc];
  if (sharpName.length === 1) return { letter: sharpName, alter: 0, octave };
  if (preferFlat) {
    const nextLetter = LETTERS[(LETTERS.indexOf(sharpName[0]) + 1) % 7];
    const nextOctave = nextLetter === 'C' ? octave + 1 : octave;
    return { letter: nextLetter, alter: -1, octave: nextOctave };
  }
  return { letter: sharpName[0], alter: 1, octave };
};

// Spell the note that sits `interval` above (or below) the root
const spellTarget = (root, interval, direction) => {
  const sign = direction === 'down' ? -1 : 1;
  const rootMidi = midiFromSpelling(root.letter, root.alter, root.octave);
  const targetMidi = rootMidi + sign * interval.semitones;

  const letterIndex = LETTERS.indexOf(root.letter) + sign * interval.degree;
  const letter = LETTERS[((letterIndex % 7) + 7) % 7];
  const octave = root.octave + Math.floor(letterIndex / 7);
  const alter = targetMidi - midiFromSpelling(letter, 0, octave);

  if (Math.abs(alter) > 2) {
    return { ...simpleSpelling(targetMidi, root.alter < 0 || sign < 0), midi: targetMidi };
  }
  return { letter, alter, octave, midi: targetMidi };
};

const IntervalProduction = () => {
  const { t } = useLanguage();
  const { user } = useAuth();

  const [gameState, setGameState] = useState('setup'); // 'setup' | 'playing' | 'finished'
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [prompt, setPrompt] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [roundResults, setRoundResults] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadedInstrument, setLoadedInstrument] = useState(null);
  const [currentRandomInstrument, setCurrentRandomInstrument] = useState(null);

  const [config, setConfig] = useState({
    direction: 'both', // 'up' | 'down' | 'both'
    questionCount: 10,
    enabledIntervals: new Set(['major2nd', 'major3rd', 'perfect4th', 'perfect5th', 'octave']),
    rootPool: 'naturals', // 'naturals' | 'chromatic'
    enabledOctaves: new Set([3, 4]),
    instrument: 'piano',
    volume: 0,
    autoPlayReference: true
  });

  const notationRef = useRef(null);
  const instrumentRef = useRef(null);
  const configRef = useRef(config);
  configRef.current = config;

  const instruments = {
    random: { name: `🎲 ${t('earTraining.instruments.random')}`, soundfont: null },
    piano: { name: `🎹 ${t('earTraining.instruments.piano')}`, soundfont: 'piano' },
    electric_piano: { name: `🎹 ${t('earTraining.instruments.electricPiano')}`, soundfont: 'electric_piano' },
    guitar: { name: `🎸 ${t('earTraining.instruments.guitar')}`, soundfont: 'guitar' },
    bass: { name: `🎸 ${t('earTraining.instruments.bass')}`, soundfont: 'bass' },
    strings: { name: `🎻 ${t('earTraining.instruments.strings')}`, soundfont: 'strings' },
    violin: { name: `🎻 ${t('earTraining.instruments.violin')}`, soundfont: 'violin' },
    cello: { name: `🎻 ${t('earTraining.instruments.cello')}`, soundfont: 'cello' },
    brass: { name: `🎺 ${t('earTraining.instruments.brass')}`, soundfont: 'brass' },
    french_horn: { name: `🎺 ${t('earTraining.instruments.frenchHorn')}`, soundfont: 'french_horn' },
    trombone: { name: `🎺 ${t('earTraining.instruments.trombone')}`, soundfont: 'trombone' },
    saxophone: { name: `🎷 ${t('earTraining.instruments.saxophone')}`, soundfont: 'saxophone' },
    flute: { name: `🎵 ${t('earTraining.instruments.flute')}`, soundfont: 'flute' },
    clarinet: { name: `🎵 ${t('earTraining.instruments.clarinet')}`, soundfont: 'clarinet' },
    oboe: { name: `🎵 ${t('earTraining.instruments.oboe')}`, soundfont: 'oboe' },
    organ: { name: `🎼 ${t('earTraining.instruments.organ')}`, soundfont: 'organ' },
    choir: { name: `👥 ${t('earTraining.instruments.choir')}`, soundfont: 'choir' },
    bells: { name: `🔔 ${t('earTraining.instruments.bells')}`, soundfont: 'bells' },
    harp: { name: `🪕 ${t('earTraining.instruments.harp')}`, soundfont: 'harp' }
  };

  const randomInstrumentList = Object.keys(instruments).filter((key) => key !== 'random');

  // semitones = size in half steps, degree = diatonic steps (0 = unison, 4 = 5th)
  const intervals = [
    { id: 'minor2nd', name: t('earTraining.intervals.minor2nd'), shortName: 'm2', semitones: 1, degree: 1 },
    { id: 'major2nd', name: t('earTraining.intervals.major2nd'), shortName: 'M2', semitones: 2, degree: 1 },
    { id: 'minor3rd', name: t('earTraining.intervals.minor3rd'), shortName: 'm3', semitones: 3, degree: 2 },
    { id: 'major3rd', name: t('earTraining.intervals.major3rd'), shortName: 'M3', semitones: 4, degree: 2 },
    { id: 'perfect4th', name: t('earTraining.intervals.perfect4th'), shortName: 'P4', semitones: 5, degree: 3 },
    { id: 'tritone', name: t('earTraining.intervals.tritone'), shortName: 'TT', semitones: 6, degree: 3 },
    { id: 'perfect5th', name: t('earTraining.intervals.perfect5th'), shortName: 'P5', semitones: 7, degree: 4 },
    { id: 'minor6th', name: t('earTraining.intervals.minor6th'), shortName: 'm6', semitones: 8, degree: 5 },
    { id: 'major6th', name: t('earTraining.intervals.major6th'), shortName: 'M6', semitones: 9, degree: 5 },
    { id: 'minor7th', name: t('earTraining.intervals.minor7th'), shortName: 'm7', semitones: 10, degree: 6 },
    { id: 'major7th', name: t('earTraining.intervals.major7th'), shortName: 'M7', semitones: 11, degree: 6 },
    { id: 'octave', name: t('earTraining.intervals.octave'), shortName: '8va', semitones: 12, degree: 7 },
    { id: 'minor9th', name: t('earTraining.intervals.minor9th'), shortName: 'm9', semitones: 13, degree: 8 },
    { id: 'major9th', name: t('earTraining.intervals.major9th'), shortName: 'M9', semitones: 14, degree: 8 },
    { id: 'minor10th', name: t('earTraining.intervals.minor10th'), shortName: 'm10', semitones: 15, degree: 9 },
    { id: 'major10th', name: t('earTraining.intervals.major10th'), shortName: 'M10', semitones: 16, degree: 9 },
    { id: 'perfect11th', name: t('earTraining.intervals.perfect11th'), shortName: 'P11', semitones: 17, degree: 10 },
    { id: 'perfect12th', name: t('earTraining.intervals.perfect12th'), shortName: 'P12', semitones: 19, degree: 11 }
  ];

  const difficultyPresets = {
    easy: {
      name: t('earTraining.difficulty.beginner'),
      intervals: new Set(['major2nd', 'major3rd', 'perfect5th', 'octave']),
      octaves: new Set([3, 4])
    },
    medium: {
      name: t('earTraining.difficulty.intermediate'),
      intervals: new Set(['major2nd', 'minor3rd', 'major3rd', 'perfect4th', 'perfect5th', 'major6th', 'octave']),
      octaves: new Set([3, 4])
    },
    hard: {
      name: t('earTraining.difficulty.advanced'),
      intervals: new Set(['minor2nd', 'major2nd', 'minor3rd', 'major3rd', 'perfect4th', 'tritone', 'perfect5th', 'minor6th', 'major6th', 'minor7th', 'major7th', 'octave']),
      octaves: new Set([2, 3, 4, 5])
    },
    killer: {
      name: t('earTraining.difficulty.killer'),
      intervals: new Set(intervals.map((i) => i.id)),
      octaves: new Set([2, 3, 4, 5])
    }
  };

  const loadCurrentInstrument = useCallback(async () => {
    const activeConfig = configRef.current;
    try {
      await Tone.start();

      let instrumentToLoad;
      if (activeConfig.instrument === 'random') {
        instrumentToLoad = randomInstrumentList[Math.floor(Math.random() * randomInstrumentList.length)];
        setCurrentRandomInstrument(instrumentToLoad);
      } else {
        instrumentToLoad = activeConfig.instrument;
        setCurrentRandomInstrument(null);
      }

      const instrument = await loadSoundfontInstrument(
        instruments[instrumentToLoad].soundfont,
        activeConfig.volume
      );
      instrumentRef.current = instrument;
      setLoadedInstrument(instrument);
      return instrument;
    } catch (error) {
      const fallback = new Tone.Synth({ volume: activeConfig.volume }).toDestination();
      instrumentRef.current = fallback;
      setLoadedInstrument(fallback);
      return fallback;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const playNotes = useCallback(async (midiNotes, { duration = 1, gap = 0 } = {}) => {
    const instrument = instrumentRef.current;
    if (!instrument || midiNotes.length === 0) return;

    setIsPlaying(true);
    try {
      await Tone.start();
      const start = Tone.now();
      midiNotes.forEach((midi, index) => {
        instrument.triggerAttackRelease(
          midiToSharpName(midi),
          duration,
          start + index * (gap === 0 ? 0 : duration + gap)
        );
      });

      const total = gap === 0
        ? duration
        : duration * midiNotes.length + gap * (midiNotes.length - 1);
      setTimeout(() => setIsPlaying(false), total * 1000);
    } catch (error) {
      setIsPlaying(false);
    }
  }, []);

  const generateMusicXML = (question) => {
    const notes = [question.root, question.target];
    const lowest = Math.min(question.root.midi, question.target.midi);
    const highest = Math.max(question.root.midi, question.target.midi);
    // Bass clef when the pair sits mostly below middle C
    const useBassClef = (lowest + highest) / 2 < 60;

    const noteXML = notes
      .map(
        (note) => `      <note>
        <pitch>
          <step>${note.letter}</step>
          ${note.alter !== 0 ? `<alter>${note.alter}</alter>` : ''}
          <octave>${note.octave}</octave>
        </pitch>
        <duration>8</duration>
        <type>half</type>
      </note>`
      )
      .join('\n');

    return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 3.1 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
  <part-list>
    <score-part id="P1">
      <part-name>Interval Production</part-name>
    </score-part>
  </part-list>
  <part id="P1">
    <measure number="1">
      <attributes>
        <divisions>4</divisions>
        <key>
          <fifths>0</fifths>
        </key>
        <time>
          <beats>4</beats>
          <beat-type>4</beat-type>
        </time>
        <clef>
          <sign>${useBassClef ? 'F' : 'G'}</sign>
          <line>${useBassClef ? '4' : '2'}</line>
        </clef>
      </attributes>
${noteXML}
    </measure>
  </part>
</score-partwise>`;
  };

  const generateQuestion = async () => {
    const availableIntervals = intervals.filter((i) => config.enabledIntervals.has(i.id));
    if (availableIntervals.length === 0) return null;

    const interval = availableIntervals[Math.floor(Math.random() * availableIntervals.length)];

    const octaves = Array.from(config.enabledOctaves);
    if (octaves.length === 0) octaves.push(4);
    const octave = octaves[Math.floor(Math.random() * octaves.length)];

    const rootPool = config.rootPool === 'chromatic' ? CHROMATIC_ROOTS : NATURAL_ROOTS;
    const rootSpelling = rootPool[Math.floor(Math.random() * rootPool.length)];

    const direction = config.direction === 'both'
      ? (Math.random() < 0.5 ? 'up' : 'down')
      : config.direction;

    const root = {
      ...rootSpelling,
      octave,
      midi: midiFromSpelling(rootSpelling.letter, rootSpelling.alter, octave)
    };
    const target = spellTarget(root, interval, direction);

    await loadCurrentInstrument();

    const question = { interval, direction, root, target };
    setPrompt(question);
    setRevealed(false);
    return question;
  };

  const startGame = async () => {
    setGameState('playing');
    setCurrentQuestion(0);
    setScore({ correct: 0, total: 0 });
    setRoundResults([]);

    await logEarTrainingStart(user, 'Interval Production', config);
    await generateQuestion();
  };

  const resetToSetup = () => {
    setGameState('setup');
    setCurrentQuestion(0);
    setScore({ correct: 0, total: 0 });
    setRoundResults([]);
    setPrompt(null);
    setRevealed(false);
  };

  const handleSelfAssessment = async (wasCorrect) => {
    const newScore = {
      correct: score.correct + (wasCorrect ? 1 : 0),
      total: score.total + 1
    };
    setScore(newScore);

    const results = [
      ...roundResults,
      {
        question: currentQuestion + 1,
        interval: prompt.interval,
        direction: prompt.direction,
        root: spellingToDisplay(prompt.root),
        target: spellingToDisplay(prompt.target),
        correct: wasCorrect
      }
    ];
    setRoundResults(results);

    if (currentQuestion + 1 >= config.questionCount) {
      setGameState('finished');
      await logEarTrainingComplete(user, 'Interval Production', {
        correct: newScore.correct,
        total: newScore.total,
        roundResults: results
      });
    } else {
      setCurrentQuestion((prev) => prev + 1);
      await generateQuestion();
    }
  };

  const applyPreset = (presetKey) => {
    const preset = difficultyPresets[presetKey];
    if (!preset) return;
    setConfig((prev) => ({
      ...prev,
      enabledIntervals: new Set(preset.intervals),
      enabledOctaves: new Set(preset.octaves)
    }));
  };

  const toggleInterval = (intervalId) => {
    setConfig((prev) => {
      const next = new Set(prev.enabledIntervals);
      if (next.has(intervalId)) {
        next.delete(intervalId);
      } else {
        next.add(intervalId);
      }
      return { ...prev, enabledIntervals: next };
    });
  };

  // Reload the instrument when the sound settings change mid-round
  useEffect(() => {
    if (gameState === 'playing') {
      loadCurrentInstrument();
    }
  }, [config.instrument, config.volume]); // eslint-disable-line react-hooks/exhaustive-deps

  // Play the reference pitch as soon as a new question appears
  useEffect(() => {
    if (gameState !== 'playing' || !prompt || revealed || !config.autoPlayReference) return;
    const timer = setTimeout(() => playNotes([prompt.root.midi], { duration: 2 }), 300);
    return () => clearTimeout(timer);
  }, [prompt, gameState, revealed, config.autoPlayReference, playNotes]);

  // Draw the staff as soon as the question appears - seeing the gap while
  // singing it is the point of the drill, and the interval is already named
  useEffect(() => {
    if (!prompt || !notationRef.current) return;

    let cancelled = false;
    const container = notationRef.current;
    container.innerHTML = '';

    const osmd = new OpenSheetMusicDisplay(container, {
      autoResize: true,
      backend: 'svg',
      drawTitle: false,
      drawPartNames: false,
      // Trims OSMD's page margins so the staff fits without clipping ledger lines
      drawingParameters: 'compacttight'
    });
    osmd.zoom = window.innerWidth <= 768 ? 0.5 : 0.6;

    osmd
      .load(generateMusicXML(prompt))
      .then(() => {
        if (!cancelled) osmd.render();
      })
      .catch((error) => console.error('Error rendering interval production notation:', error));

    return () => {
      cancelled = true;
      try {
        osmd.clear();
      } catch (e) {
        // Container may already be gone
      }
    };
  }, [prompt]);

  useEffect(() => {
    return () => {
      if (instrumentRef.current && instrumentRef.current.dispose) {
        instrumentRef.current.dispose();
      }
    };
  }, []);

  const renderConfigArea = () => {
    const isConfigDisabled = gameState === 'playing';

    return (
      <div className={`interval-config ${isConfigDisabled ? 'disabled' : ''}`}>
        <div className="config-header">
          <h4>{t('earTraining.config.title')}</h4>
        </div>

        <div className="config-section">
          <label>{t('earTraining.activities.intervalProduction.config.singDirection')}:</label>
          <div className="mode-buttons">
            {[
              { value: 'up', label: `↗️ ${t('earTraining.directions.ascending')}` },
              { value: 'down', label: `↙️ ${t('earTraining.directions.descending')}` },
              { value: 'both', label: `↕️ ${t('earTraining.directions.both')}` }
            ].map((option) => (
              <button
                key={option.value}
                className={`mode-btn ${config.direction === option.value ? 'active' : ''}`}
                onClick={() => setConfig((prev) => ({ ...prev, direction: option.value }))}
                disabled={isConfigDisabled}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="config-section">
          <label>{t('earTraining.activities.intervalProduction.config.startingPitches')}:</label>
          <div className="mode-buttons">
            {[
              { value: 'naturals', label: t('earTraining.activities.intervalProduction.config.naturalsOnly') },
              { value: 'chromatic', label: t('earTraining.activities.intervalProduction.config.allTwelve') }
            ].map((option) => (
              <button
                key={option.value}
                className={`mode-btn ${config.rootPool === option.value ? 'active' : ''}`}
                onClick={() => setConfig((prev) => ({ ...prev, rootPool: option.value }))}
                disabled={isConfigDisabled}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="config-section">
          <label>{t('earTraining.config.instrument')}:</label>
          <select
            value={config.instrument}
            onChange={(e) => setConfig((prev) => ({ ...prev, instrument: e.target.value }))}
          >
            {Object.entries(instruments).map(([key, instrument]) => (
              <option key={key} value={key}>
                {instrument.name}
              </option>
            ))}
          </select>
        </div>

        <div className="config-section">
          <label>{t('earTraining.config.volume')}:</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="range"
              min="-40"
              max="6"
              step="1"
              value={config.volume}
              onChange={(e) => setConfig((prev) => ({ ...prev, volume: Number(e.target.value) }))}
              style={{ flex: 1 }}
            />
            <span className="volume-value" style={{ minWidth: '50px', fontSize: '12px' }}>
              {config.volume} dB
            </span>
          </div>
        </div>

        <div className="config-section">
          <label>{t('earTraining.activities.intervalProduction.config.startingOctaves')}:</label>
          <div className="interval-checkboxes-compact">
            {[2, 3, 4, 5].map((octave) => (
              <label key={octave} className="interval-checkbox-compact" title={`Octave ${octave}`}>
                <input
                  type="checkbox"
                  checked={config.enabledOctaves.has(octave)}
                  onChange={() => {
                    const next = new Set(config.enabledOctaves);
                    if (next.has(octave)) {
                      if (next.size > 1) next.delete(octave);
                    } else {
                      next.add(octave);
                    }
                    setConfig((prev) => ({ ...prev, enabledOctaves: next }));
                  }}
                  disabled={isConfigDisabled}
                />
                <span>{octave}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="config-section">
          <label>{t('earTraining.config.questions')}:</label>
          <select
            value={config.questionCount}
            onChange={(e) => setConfig((prev) => ({ ...prev, questionCount: parseInt(e.target.value, 10) }))}
            disabled={isConfigDisabled}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
          </select>
        </div>

        <div className="config-section">
          <label className="interval-checkbox-compact" style={{ display: 'inline-flex' }}>
            <input
              type="checkbox"
              checked={config.autoPlayReference}
              onChange={() => setConfig((prev) => ({ ...prev, autoPlayReference: !prev.autoPlayReference }))}
            />
            <span>{t('earTraining.activities.intervalProduction.config.autoPlayReference')}</span>
          </label>
        </div>

        <div className="config-section">
          <label>{t('earTraining.config.difficultyPresets')}:</label>
          <div className="preset-buttons">
            {Object.entries(difficultyPresets).map(([key, preset]) => (
              <button
                key={key}
                className="preset-btn"
                onClick={() => applyPreset(key)}
                disabled={isConfigDisabled}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>

        <div className="config-section">
          <label>{t('earTraining.config.intervalsToInclude')}:</label>
          <div className="interval-checkboxes-compact">
            {intervals.map((interval) => (
              <label key={interval.id} className="interval-checkbox-compact" title={interval.name}>
                <input
                  type="checkbox"
                  checked={config.enabledIntervals.has(interval.id)}
                  onChange={() => toggleInterval(interval.id)}
                  disabled={isConfigDisabled}
                />
                <span>{interval.shortName}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderPlayArea = () => {
    if (gameState === 'setup') {
      return (
        <div className="play-area setup">
          <div className="setup-message">
            <h3>{t('earTraining.activities.intervalProduction.readyToSing')}</h3>
            <p>{t('earTraining.activities.intervalProduction.setupHint')}</p>
            <button
              className="start-game-btn"
              onClick={startGame}
              disabled={config.enabledIntervals.size === 0}
            >
              {t('earTraining.setup.startTraining')}
            </button>
          </div>
        </div>
      );
    }

    if (gameState === 'finished') {
      return (
        <div className="play-area finished">
          <div className="results-summary">
            <h3>{t('earTraining.results.roundComplete')}</h3>
            <div className="final-score">
              <div className="score-circle">
                <span className="score-percentage">{Math.round((score.correct / score.total) * 100)}%</span>
                <span className="score-fraction">{score.correct}/{score.total}</span>
              </div>
            </div>
            <div className="results-breakdown">
              {roundResults.map((result, index) => (
                <div key={index} className={`result-item ${result.correct ? 'correct' : 'incorrect'}`}>
                  <span className="question-num">{t('earTraining.results.questionAbbrev')}{result.question}</span>
                  <span className="interval-name">
                    {result.interval.name} {result.direction === 'down' ? '↙️' : '↗️'} {result.root} → {result.target}
                  </span>
                  <span className="result-icon">{result.correct ? '✅' : '❌'}</span>
                </div>
              ))}
            </div>
            <div className="result-actions">
              <button className="play-again-btn" onClick={startGame}>{t('earTraining.results.playAgain')}</button>
              <button className="setup-btn" onClick={resetToSetup}>{t('earTraining.results.changeSettings')}</button>
            </div>
          </div>
        </div>
      );
    }

    const directionLabel = prompt?.direction === 'down'
      ? `↙️ ${t('earTraining.activities.intervalProduction.down')}`
      : `↗️ ${t('earTraining.activities.intervalProduction.up')}`;

    return (
      <div className="play-area playing">
        <div className="game-header">
          <div className="progress">
            {t('earTraining.progress', { current: currentQuestion + 1, total: config.questionCount })}
            {config.instrument === 'random' && currentRandomInstrument && (
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                {instruments[currentRandomInstrument]?.name}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="current-score">
              <span className="score-label">{t('earTraining.score')}:</span>
              <span className="score-value">{score.correct}/{score.total}</span>
            </div>
            <button
              className="production-quit-btn"
              onClick={resetToSetup}
              title={t('earTraining.gameplay.quitAndReturn')}
            >
              {t('earTraining.gameplay.quit')}
            </button>
          </div>
        </div>

        <div className="production-prompt">
          <span className="production-instruction">
            {t('earTraining.activities.intervalProduction.singPrompt')}
          </span>
          <span className="production-interval">{prompt?.interval.name}</span>
          <span className="production-direction">{directionLabel}</span>
          <span className="production-instruction">
            {t('earTraining.activities.intervalProduction.from')}
          </span>
          <span className="production-root-note">{prompt ? spellingToDisplay(prompt.root) : ''}</span>
          {prompt && (
            <>
              <span className="production-arrow">→</span>
              <span className="production-target-note">{spellingToDisplay(prompt.target)}</span>
            </>
          )}
        </div>

        {prompt && (
          <div className="notation-container production-notation">
            <div ref={notationRef} className="interval-notation" />
          </div>
        )}

        <div className="production-controls">
          <button
            className="production-play-btn"
            onClick={() => prompt && playNotes([prompt.root.midi], { duration: 2 })}
            disabled={!prompt || isPlaying || !loadedInstrument}
          >
            {isPlaying
              ? t('earTraining.gameplay.playing')
              : `🔊 ${t('earTraining.activities.intervalProduction.playStartingPitch')}`}
          </button>

          {!revealed ? (
            <button
              className="production-reveal-btn"
              onClick={() => {
                setRevealed(true);
                if (prompt) playNotes([prompt.root.midi, prompt.target.midi], { duration: 1, gap: 0.15 });
              }}
              disabled={!prompt || !loadedInstrument}
            >
              🎯 {t('earTraining.activities.intervalProduction.hearAnswer')}
            </button>
          ) : (
            <>
              <button
                className="production-play-btn"
                onClick={() => playNotes([prompt.target.midi], { duration: 2 })}
                disabled={isPlaying}
              >
                🎯 {t('earTraining.activities.intervalProduction.playTargetNote')}
              </button>
              <button
                className="production-play-btn"
                onClick={() => playNotes([prompt.root.midi, prompt.target.midi], { duration: 1, gap: 0.15 })}
                disabled={isPlaying}
              >
                🎶 {t('earTraining.activities.intervalProduction.playBoth')}
              </button>
              <button
                className="production-play-btn"
                onClick={() => playNotes([prompt.root.midi, prompt.target.midi], { duration: 2 })}
                disabled={isPlaying}
              >
                🎵 {t('earTraining.activities.intervalProduction.playTogether')}
              </button>
            </>
          )}
        </div>

        {revealed && prompt && (
          <div className="production-assessment">
            <span className="production-assessment-label">
              {t('earTraining.activities.intervalProduction.howDidYouDo')}
            </span>
            <button className="assessment-btn nailed" onClick={() => handleSelfAssessment(true)}>
              ✅ {t('earTraining.activities.intervalProduction.nailedIt')}
            </button>
            <button className="assessment-btn missed" onClick={() => handleSelfAssessment(false)}>
              ❌ {t('earTraining.activities.intervalProduction.missedIt')}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="interval-training-container interval-production-container">
      <div className="desktop-layout">
        <CollapsibleSection
          title={t('earTraining.config.title')}
          defaultExpanded={false}
          className="config-section-mobile"
        >
          {renderConfigArea()}
        </CollapsibleSection>

        <div className="config-area-desktop">
          {renderConfigArea()}
        </div>

        <div className="play-area-container">
          {renderPlayArea()}
        </div>
      </div>
    </div>
  );
};

export default IntervalProduction;
